import { ReactElement, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import useTabVisibility from '~/hooks/browser/useTabVisibility';
import useIsOnline from '~/hooks/network/useIsOnline';
import { getAccessToken } from '~/services/localStorage/auth';
import { useStoreActions, useStoreState } from '~/store/hooks';
import isDev from '~/utils/isDev';

interface ISignalRTokenContext {
  token: string | null;
  isOnline: boolean;
  handleError: (error?: Error) => Promise<void>;
  handleConnection: () => void;
}

const SignalRTokenContext = createContext<ISignalRTokenContext | null>(null);

interface IProps {
  children: ReactNode | ReactNode[];
}

let tries = 0;

export const SignalRTokenProvider = ({ children }: IProps): ReactElement => {
  const [token, setToken] = useState<string | null>(null);
  const { current } = useStoreState(state => state.user);
  const isOnline = useIsOnline();
  const isTabActive = useTabVisibility();

  const { onGetCurrentProfile } = useStoreActions(state => state.user);

  const connectionCloseDelay = () =>
    new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });

  // eslint-disable-next-line consistent-return
  const handleError = async (error?: Error) => {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('error: ', JSON.stringify(error));
    }
    if (tries >= 5) {
      return connectionCloseDelay();
    }
    // Trigger Axios interceptor to refresh the token upon encountering an error and tab is active.
    // The error can stem from various issues, such as a loss of internet connectivity, server unreachability, or a SignalR service outage.
    // A maximum of 5 retry attempts will be made. The retry counter will be reset upon a page refresh or restoration of network connectivity.
    // If the token refresh process fails, the user will be logged out by the Axios interceptor.
    if (!isTabActive) {
      return undefined;
    }
    await onGetCurrentProfile();
    tries += 1;
    setToken(null);
  };

  const handleConnection = (): void => {
    tries = 0;
  };

  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken && !token && current) {
      setToken(accessToken);
    } else if (!accessToken) {
      setToken(null);
    }
  }, [token, current]);

  useEffect(() => {
    if (isOnline) {
      tries = 0;
      setToken(null);
    }
  }, [isOnline]);

  const contextValue: ISignalRTokenContext = {
    token,
    isOnline,
    handleError,
    handleConnection,
  };

  return (
    <SignalRTokenContext.Provider value={contextValue}>{children}</SignalRTokenContext.Provider>
  );
};

export const useSignalRToken = (): ISignalRTokenContext => {
  const context = useContext(SignalRTokenContext);
  if (!context) {
    throw new Error('useSignalRToken must be used within a SignalRTokenProvider');
  }
  return context;
};
